import { IAudioItem } from 'types/audio'
import { IMediaItem, MediaStatus, TMediaItemType } from 'types/media'
import { isVisualMediaItem } from 'utils/media'
import { isPlayheadOverItem } from 'utils/timeline'
import { isBlobUrl } from 'utils'
import { videoTypes } from 'consts'
import {
  ICanvasObject,
  IObjectDimensionsResolvedEventData,
  ITextMetadata,
  ProcessingEvent,
  WebWoodEngine
} from '@renderforest/webwood-engine'
import { logError } from 'utils/logger'

export const getTimeLineItemStartTime = (item: IMediaItem | IAudioItem) => {
  if (isVisualMediaItem(item.type)) {
    return (item as IMediaItem).startTime
  }
  return (item as IAudioItem).projectStart
}

export const isTimelineItemOverlappedWithPlayhead = (
  timelineItem: IMediaItem | IAudioItem,
  playheadTime: number
) => {
  const itemStartTime = getTimeLineItemStartTime(timelineItem)

  return isPlayheadOverItem({
    itemStartTime,
    itemDuration: timelineItem.duration,
    playheadTime
  })
}

export const findVideoItemsOverlappingWithPlayhead = (
  visualMediaItems: IMediaItem[],
  playheadTime: number
): IMediaItem[] | undefined => {
  // filt video items with ready status
  const videoItemsWithReadyStatus = visualMediaItems.filter(
    item =>
      videoTypes.includes(item.type) && item.meta.status === MediaStatus.OK
  )

  // filter out blob URLs
  const videoItems = videoItemsWithReadyStatus.filter(
    item => !isBlobUrl(item.meta.path)
  )

  // find video items overlapping with playhead
  const overlappingItems = videoItems.filter(item =>
    isTimelineItemOverlappedWithPlayhead(item, playheadTime)
  )

  return overlappingItems
}

export const waitForTexObjectDimensionsUpdate = async (
  newConfig: ICanvasObject<ITextMetadata>
): Promise<IObjectDimensionsResolvedEventData | null> => {
  const engine = WebWoodEngine.getInstance()
  if (!engine) {
    logError('Engine not found')
    return null
  }
  const promise = new Promise<IObjectDimensionsResolvedEventData>(resolve => {
    const handler = (data: IObjectDimensionsResolvedEventData) => {
      const expectedFontSize = newConfig.metadata?.style?.fontSize ?? 0
      const expectedStrokeThickness =
        newConfig.metadata?.style?.stroke?.thickness ?? 0
      const receivedStrokeThickness = data.strokeThickness ?? 0
      if (
        data.id !== newConfig.originalId ||
        data.fontSize !== expectedFontSize ||
        receivedStrokeThickness !== expectedStrokeThickness
      ) {
        return
      }
      resolve(data)
      engine.signals.off(ProcessingEvent.OBJECT_DISPLAY_SIZE_CHANGED, handler)
    }
    engine.signals.on(ProcessingEvent.OBJECT_DISPLAY_SIZE_CHANGED, handler)
  })
  engine.updateObjectById(newConfig.id, newConfig)
  return promise
}

export const getItemsToUpdate = (
  lastNewItem: IMediaItem,
  newItemsLastIndex: number,
  currentLineItems: IMediaItem[]
) => {
  let lastEndTime = lastNewItem.startTime + lastNewItem.duration

  const itemsToUpdate: Array<{
    id: string
    left: number
    type: TMediaItemType
  }> = []

  for (let i = newItemsLastIndex + 1; i < currentLineItems.length; i++) {
    const nextItem = currentLineItems[i]
    if (!nextItem || nextItem.startTime >= lastEndTime) break

    const adjustedStart = lastEndTime
    lastEndTime = adjustedStart + nextItem.duration

    itemsToUpdate.push({
      id: nextItem.id,
      left: adjustedStart,
      type: nextItem.type
    })
  }
  return itemsToUpdate
}
